import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { logger } from 'lib/logger';
import { serviceRoleApi } from 'api';
import {
  SERVICE_AGREEMENT_LIST_QUERY_KEY,
  getServiceAgreementDetailsQueryKey,
  getServiceAgreementHistoryQueryKey,
} from './privateBanking';
import { getContactNotesQueryKey } from './contactNotes';
import { SafeDepositBoxFields, SafeDepositBoxServiceRoleBulkItem } from 'pco-product-service-internal-consumer';

type SaveServiceRolesRequest = {
  serviceAgreementUid: string;
  partnerIds: string[];
  relatedServiceAgreementUids?: string[];
  contactNoteUids?: string[];
};

export const useSaveServiceRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceAgreementUid, partnerIds, contactNoteUids }: SaveServiceRolesRequest) => {
      const logContext = {
        serviceAgreementUid,
        partnerCount: partnerIds.length,
        contactNoteCount: contactNoteUids?.length ?? 0,
      };

      logger.info('Saving service roles', logContext);

      try {
        const serviceAgreement = contactNoteUids
          ? await serviceRoleApi.syncServiceRolesBulkWithContactNotes({
              serviceAgreementUid,
              serviceRoleBulkSyncRequest: { partnerUids: partnerIds, contactNoteUids },
            })
          : await serviceRoleApi.syncServiceRolesBulk({ serviceAgreementUid, requestBody: partnerIds });

        logger.info('Saved service roles', logContext);

        return serviceAgreement;
      } catch (error) {
        logger.error('Failed to save service roles', logContext, error);
        throw error;
      }
    },
    onSuccess: async (serviceAgreement, request) => {
      const parentServiceAgreementUid = request.serviceAgreementUid;
      const allServiceAgreementUids = [parentServiceAgreementUid, ...(request.relatedServiceAgreementUids ?? [])];
      const parentServiceAgreementDetailsQueryKey = getServiceAgreementDetailsQueryKey(parentServiceAgreementUid);

      queryClient.setQueryData(parentServiceAgreementDetailsQueryKey, serviceAgreement);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] }),
        ...allServiceAgreementUids.map((serviceAgreementUid) =>
          queryClient.invalidateQueries({ queryKey: getServiceAgreementDetailsQueryKey(serviceAgreementUid) }),
        ),
        ...allServiceAgreementUids.map((serviceAgreementUid) =>
          queryClient.invalidateQueries({
            queryKey: getServiceAgreementHistoryQueryKey(serviceAgreementUid),
            refetchType: 'all',
          }),
        ),
        queryClient.invalidateQueries({ queryKey: getContactNotesQueryKey(parentServiceAgreementUid) }),
        queryClient.invalidateQueries({ queryKey: ['partnerSearchByBr'] }),
      ]);
    },
  });
};

export const getSafeDepositBoxServiceRoleOptionsQueryKey = (serviceAgreementUid: string | undefined) =>
  ['safeDepositBoxServiceRoleOptions', serviceAgreementUid] as const;

export const useGetSafeDepositBoxServiceRoleOptions = (serviceAgreementUid: string | undefined) => {
  return useQuery({
    queryKey: getSafeDepositBoxServiceRoleOptionsQueryKey(serviceAgreementUid),
    queryFn: async () => {
      if (!serviceAgreementUid) throw new Error('serviceAgreementUid is required');
      return await serviceRoleApi.getSafeDepositBoxServiceRoleOptions({ serviceAgreementUid });
    },
    enabled: !!serviceAgreementUid,
  });
};

type SaveSafeDepositBoxServiceRolesRequest = {
  serviceAgreementUid: string;
  serviceRoles: SafeDepositBoxServiceRoleBulkItem[];
  safeDepositBoxDetails?: SafeDepositBoxFields;
};

export const useSaveSafeDepositBoxServiceRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      serviceAgreementUid,
      serviceRoles,
      safeDepositBoxDetails,
    }: SaveSafeDepositBoxServiceRolesRequest) => {
      logger.info('Saving Safe Deposit Box service roles', {
        serviceAgreementUid,
        serviceRoleCount: serviceRoles.length,
        configurationChanged: !!safeDepositBoxDetails,
      });

      return await serviceRoleApi.syncSafeDepositBoxServiceRolesBulk({
        serviceAgreementUid,
        safeDepositBoxBulkSyncRequest: { safeDepositBoxDetails, serviceRoles },
      });
    },
    onSuccess: async (serviceAgreement, request) => {
      queryClient.setQueryData(getServiceAgreementDetailsQueryKey(request.serviceAgreementUid), serviceAgreement);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: getServiceAgreementDetailsQueryKey(request.serviceAgreementUid) }),
        queryClient.invalidateQueries({
          queryKey: getServiceAgreementHistoryQueryKey(request.serviceAgreementUid),
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({ queryKey: ['partnerSearchByBr'] }),
      ]);
    },
  });
};
